import { apiClient, type ApiResponse } from './client';

// Types for messaging
export type MessageType = 'text' | 'trade_activity';

export type TradeAction =
  | 'trade_proposed'
  | 'trade_countered'
  | 'trade_accepted'
  | 'trade_declined'
  | 'trade_cancelled'
  | 'trade_completed'
  | 'trade_signing_ready'
  | 'trade_invalidated';

export interface Conversation {
  id: string;
  participant_1: string;
  participant_2: string;
  last_message_at: string | null;
  last_message_preview: string | null;
  last_message_sender: string | null;
  participant_1_last_read_at: string | null;
  participant_2_last_read_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ConversationWithDetails extends Conversation {
  other_participant: string;
  other_participant_display_name: string | null;
  other_participant_project_slug: string | null;
  unread_count: number;
}

export interface Message {
  id: string;
  conversation_id: string;
  sender_address: string;
  content: string;
  message_type: MessageType;
  trade_id: string | null;
  trade_action: TradeAction | null;
  trade_version: number | null;
  created_at: string;
  deleted_at: string | null;
  sender_display_name: string | null;
}

// API Response types
interface InboxResponse {
  success: boolean;
  conversations: ConversationWithDetails[];
}

interface MessagesResponse {
  success: boolean;
  messages: Message[];
}

interface ConversationResponse {
  success: boolean;
  conversation: Conversation;
}

interface SendMessageResponse {
  success: boolean;
  message: Message;
  conversationId?: string;
}

interface UnreadCountResponse {
  success: boolean;
  unread_count: number;
}

/**
 * Get inbox (all conversations) for the current user
 * @param limit - Max conversations to return (default 50)
 * @param offset - Pagination offset (default 0)
 */
export async function getInbox(limit = 50, offset = 0): Promise<ConversationWithDetails[]> {
  const response = await apiClient.get<InboxResponse>(
    `/messages/inbox?limit=${limit}&offset=${offset}`
  );

  if (!response.success) {
    throw new Error('Failed to fetch inbox');
  }

  return response.conversations;
}

/**
 * Get total unread message count across all conversations
 */
export async function getUnreadMessageCount(): Promise<number> {
  const response = await apiClient.get<UnreadCountResponse>('/messages/unread-count');

  if (!response.success) {
    throw new Error('Failed to fetch unread count');
  }

  return response.unread_count;
}

/**
 * Get or start a conversation with a specific wallet
 * @param address - The wallet address to start a conversation with
 */
export async function getOrStartConversation(address: string): Promise<Conversation> {
  const response = await apiClient.get<ConversationResponse>(`/messages/with/${address}`);

  if (!response.success) {
    throw new Error('Failed to get conversation');
  }

  return response.conversation;
}

/**
 * Get messages in a specific conversation
 * @param conversationId - The conversation UUID
 * @param limit - Max messages to return (default 50)
 * @param before - Get messages before this timestamp (ISO 8601 string)
 */
export async function getMessages(
  conversationId: string,
  limit = 50,
  before?: string
): Promise<Message[]> {
  let url = `/messages/conversation/${conversationId}?limit=${limit}`;
  if (before) {
    url += `&before=${encodeURIComponent(before)}`;
  }

  const response = await apiClient.get<MessagesResponse>(url);

  if (!response.success) {
    throw new Error('Failed to fetch messages');
  }

  return response.messages;
}

/**
 * Send a new message to a wallet
 * @param recipientAddress - The wallet address to send to
 * @param content - The message content
 */
export async function sendMessage(
  recipientAddress: string,
  content: string
): Promise<{ message: Message; conversationId: string }> {
  const response = await apiClient.post<SendMessageResponse>('/messages', {
    recipientAddress,
    content,
  });

  if (!response.success || !response.message) {
    throw new Error('Failed to send message');
  }

  return {
    message: response.message,
    conversationId: response.conversationId || response.message.conversation_id,
  };
}

/**
 * Send a message to an existing conversation
 * @param conversationId - The conversation UUID
 * @param content - The message content
 */
export async function sendMessageToConversation(
  conversationId: string,
  content: string
): Promise<Message> {
  const response = await apiClient.post<SendMessageResponse>(
    `/messages/conversation/${conversationId}`,
    { content }
  );

  if (!response.success || !response.message) {
    throw new Error('Failed to send message');
  }

  return response.message;
}

/**
 * Mark a conversation as read
 * @param conversationId - The conversation UUID
 */
export async function markConversationAsRead(conversationId: string): Promise<void> {
  const response = await apiClient.patch<{ success: boolean }>(
    `/messages/conversation/${conversationId}/read`
  );

  if (!response.success) {
    throw new Error('Failed to mark conversation as read');
  }
}

// ============================================================================
// Polling & Typing Indicator APIs
// ============================================================================

interface PollResponse {
  success: boolean;
  hasNewMessages: boolean;
  newMessageCount: number;
  latestMessage: Message | null;
  typingUsers: string[];
}

/**
 * Poll a conversation for new messages and typing status
 * Lightweight endpoint for real-time updates
 * @param conversationId - The conversation UUID
 * @param since - Optional ISO 8601 timestamp to check for messages since
 */
export async function pollConversation(
  conversationId: string,
  since?: string
): Promise<PollResponse> {
  let url = `/messages/conversation/${conversationId}/poll`;
  if (since) {
    url += `?since=${encodeURIComponent(since)}`;
  }

  const response = await apiClient.get<PollResponse>(url);

  if (!response.success) {
    throw new Error('Failed to poll conversation');
  }

  return response;
}

/**
 * Set typing status for a conversation
 * Should be called periodically while the user is typing
 * @param conversationId - The conversation UUID
 */
export async function setTypingStatus(conversationId: string): Promise<void> {
  await apiClient.post<{ success: boolean }>(
    `/messages/conversation/${conversationId}/typing`
  );
}

/**
 * Clear typing status for a conversation
 * Should be called when user stops typing or sends message
 * @param conversationId - The conversation UUID
 */
export async function clearTypingStatus(conversationId: string): Promise<void> {
  await apiClient.delete<{ success: boolean }>(
    `/messages/conversation/${conversationId}/typing`
  );
}

// ============================================================================
// Trade Activity Message APIs
// ============================================================================

export interface TradeSummaryNFT {
  token_id: string;
  name: string | null;
  thumbnail_url: string | null;
  collection_name: string | null;
}

export interface TradeSummarySide {
  nfts: TradeSummaryNFT[];
  xrp_drops: string;
}

export interface TradeSummary {
  id: string;
  status: string;
  initiator_address: string;
  recipient_address: string;
  initiator_xrp_drops: string;
  recipient_xrp_drops: string;
  viewer_gives: TradeSummarySide;
  viewer_receives: TradeSummarySide;
  expires_at: string | null;
  created_at: string | null;
}

interface TradeSummaryResponse {
  success: boolean;
  summary: TradeSummary;
}

/**
 * Get trade summary for displaying in trade activity cards
 * @param tradeId - The trade UUID
 * @param version - Optional version number to get historical snapshot
 */
export async function getTradeSummary(tradeId: string, version?: number): Promise<TradeSummary> {
  let url = `/messages/trade/${tradeId}/summary`;
  if (version !== undefined) {
    url += `?version=${version}`;
  }

  const response = await apiClient.get<TradeSummaryResponse>(url);

  if (!response.success) {
    throw new Error('Failed to fetch trade summary');
  }

  return response.summary;
}
